import { useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useOrders } from '../../hooks/useOrders';
import { useUser } from '../../hooks/useUser';
import TableSkeleton from './TableSkeleton';
import NoDataFallback from '../common/NoDataFallback';

const CustomersTable = () => {
  const { user } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const { data, isLoading, error, isError } = useOrders({
    skip: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    tenantName: user?.tenantName,
    userId: user?._id,
  });

  // Transform API data first
  const transformedOrders = data?.data?.items?.map(
    (order: any, index: number) => ({
      id: order.id || index + 1,
      name: order.customer_info?.name || `ORD-${index}`,
      phone: order.customer_info?.contact_number
        ? `+${order.customer_info.contact_number}`
        : '-',
      email: order.customer_info?.customer_email || '-',
      address: order.customer_info?.full_address || '-',
    })
  );

  // Then filter the transformed data
  const filteredOrders =
    searchQuery && transformedOrders
      ? transformedOrders.filter((order: any) =>
          order.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : transformedOrders;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      headerClassName: 'custom-header',
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 180,
      headerClassName: 'custom-header',
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 150,
      headerClassName: 'custom-header',
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 1,
      minWidth: 120,
      headerClassName: 'custom-header',
    },
  ];

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (isError && error) {
    return (
      <NoDataFallback
        message="No customers found"
        description="No customers are available for the selected date range"
      />
    );
  }

  if (data?.data?.items?.length === 0 && !isLoading) {
    return (
      <NoDataFallback
        message="No customers found"
        description="No customers are available for the selected date range"
      />
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
      }}
    >
      {/* Search Input */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 2,
          mb: 2,
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontSize: '16px',
            fontWeight: '500',
            color: 'custom.textBlue',
            ml: '8px',
          }}
        >
          Customers Information
        </Typography>
        <TextField
          variant="outlined"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            sx: {
              height: '40px',
              width: '320px',
              borderRadius: '6px',
              backgroundColor: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E5E7EB',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E5E7EB',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      </Box>

      <DataGrid
        rows={filteredOrders || []}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 20, 50, 75, 100]}
        rowCount={data?.data?.total || 0}
        paginationMode="server"
        checkboxSelection={false}
        disableRowSelectionOnClick
        loading={isLoading}
        // disableColumnMenu
        sx={{
          width: '100%',
          height: '90%',
          borderRadius: '8px',
          color: 'rgba(107, 111, 128, 1)',
          '& .MuiDataGrid-root': {
            borderRadius: '8px',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-main': {
            borderRadius: '8px',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
          },
          '& .MuiDataGrid-columnHeader': {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
          },
          '& .custom-header': {
            bgcolor: '#F8F9FC',
            color: 'custom.textBlue',
            fontSize: '14px',
            fontWeight: 600,
            padding: '12px 16px',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: 'rgba(43, 45, 63, 1)',
            fontWeight: 600,
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'transparent !important',
            border: 'none !important',
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: '#E7F0FF !important',
          },
          '& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
        }}
      />
    </Box>
  );
};

export default CustomersTable;
